import { useEffect, useState } from "react";
import {
  IInferenceFactorType,
  IReferenceInfoType,
} from "../types/feedback_type";
import { Link } from "react-router-dom";
import { PAGE_NAME_HOW_TO_FIX } from "../../../../constants/common_constants";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { PiSquare, PiSquareDuotone, PiCheckSquare } from "react-icons/pi";
import { v4 as uuidv4 } from "uuid";

// アコーディオンメニューコンポーネントに渡すprops
interface FeedbackAccordionListProps {
  inferenceFactor: IInferenceFactorType;
  inferenceFactorNumber?: number;
  isFactorsChecked?: boolean;
  setIsFactorsChecked?: React.Dispatch<React.SetStateAction<boolean[]>>;
  isBoxChecked: {
    isSelected: boolean;
    isFixed: boolean;
  };
  isNotApplicable: boolean;
  children?: React.ReactNode;
}

/**
 * アコーディオンメニューを表示するコンポーネント(リスト取得対応版)
 * @param inferenceFactor - 推定要因項目
 * @param inferenceFactorNumber - 推定要因項目のindex
 * @param isFactorsChecked - 推定要因項目がチェックされているかどうか
 * @param setIsFactorsChecked - 推定要因項目のチェック状態を更新する関数
 * @param isBoxChecked - 上部チェックボックス(「fixed」・「Not Fixed」)の選択状態
 * @param isNotApplicable - 該当なしの選択状態
 * @param children - 子要素
 * @returns JSX.Element - アコーディオンメニューの表示
 */
export const FeedbackAccordionList = ({
  inferenceFactor,
  inferenceFactorNumber,
  isFactorsChecked,
  setIsFactorsChecked,
  isBoxChecked,
  isNotApplicable,
  children,
}: FeedbackAccordionListProps) => {
  // メニューが開いているかどうかを格納
  const [open, setOpen] = useState<boolean>(false);

  // メニューを選択したかどうかを格納
  const [isMenuSelected, setIsMenuSelected] = useState<boolean>(
    isFactorsChecked || false
  );

  // リストの開閉をトグル処理
  const toggleButton = () => {
    setOpen((prevState) => !prevState);
  };

  // リストの選択状態をトグル処理
  const toggleSelectMenu = () => {
    setIsMenuSelected((prevState) => !prevState);
  };

  // 上部チェックボックス(「fixed」・「Not Fixed」)の選択状態が変化したときの処理
  useEffect(() => {
    if (isBoxChecked.isSelected && !isBoxChecked.isFixed) {
      // 「直らない」が選択された時, メニューを閉じてメニューの選択状態もリセットする
      // 「Fixed」の選択が外れたのみではメニューを閉じない
      setOpen(false);
      setIsMenuSelected(false);
    }
    if (!isBoxChecked.isSelected) {
      // チェックボックスが選択されていない時, メニューの選択状態をリセットする
      setIsMenuSelected(false);
    }
  }, [isBoxChecked]);

  // 上部チェックボックス(「fixed」・「Not Fixed」)の選択状態が変化したとき または メニューの選択状態が変化したときの処理
  useEffect(() => {
    if (setIsFactorsChecked !== undefined) {
      // セット関数が渡されているとき(undefindではないとき)
      setIsFactorsChecked((prevState) =>
        // 選択されたメニューのリスト番号を参照して全体の選択状態に反映させる
        prevState.map((value: boolean, index: number) =>
          inferenceFactorNumber === index ? isMenuSelected : value
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMenuSelected]);

  useEffect(() => {
    isFactorsChecked && setIsMenuSelected(isFactorsChecked);
  }, [isFactorsChecked]);

  // 該当なしが選択された場合, 選択状態をリセットする
  useEffect(() => {
    if (isNotApplicable) {
      setIsMenuSelected(false);
    }
  }, [isNotApplicable]);

  // 別タブのHow To Fix画面に遷移する関数
  const handleJumpToAnotherTabLink = (reference: string, title: string) => {
    const keyId = uuidv4();
    const refKey = `reference_${keyId}`;
    //LocalStorageにマニュアルリンク, タイトル(参照情報のラベル)を保存する
    localStorage.setItem(refKey, reference);
    localStorage.setItem("title", title);
    //一時的に<a>タグを作成して別タブでリンクを開く
    const link = document.createElement("a");
    link.target = "_blank";
    link.href = `/${PAGE_NAME_HOW_TO_FIX}/${keyId}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 参照情報(3階層目)をレンダリングする関数
  const renderReferences = () => {
    const referenceArray =
      inferenceFactor.issueFactor.repairFactor.referenceInfo;
    return referenceArray.map(
      (referenceInfo: IReferenceInfoType, index: number) => (
        <dd className="contents" key={referenceInfo.label}>
          <div
            className={`flex items-center ${
              index + 1 !== referenceArray.length && "border-b"
            } border-gray-700 py-2 pl-16 pr-4`}
          >
            {referenceInfo.reference ? (
              referenceInfo.reference.includes("http") ? (
                // referenceに入っている文字列にhttpが入っていた場合(外部リンクの場合),
                // 青文字でlabelを返し別タブでそのURLを開くリンクをつける
                <Link
                  to={referenceInfo.reference}
                  target="_blank"
                  rel="noopener noreferrer"
                  // className="text-sub-blue line-clamp-2"
                  className="text-sub-blue"
                >
                  {referenceInfo.label}
                </Link>
              ) : (
                // referenceに文字列が入っていた場合(ページ遷移の場合),
                // 青文字でlabelをそのページに遷移するリンクをつける
                <button
                  // className="text-left text-sub-blue line-clamp-2"
                  className="text-left text-sub-blue"
                  onClick={() =>
                    handleJumpToAnotherTabLink(
                      referenceInfo.reference,
                      referenceInfo.label
                    )
                  }
                >
                  {referenceInfo.label}
                </button>
              )
            ) : (
              //  referenceに何もない場合, 黒文字のままlabelを返す
              // <p className="line-clamp-2">{referenceInfo.label}</p>
              <p>{referenceInfo.label}</p>
            )}
          </div>
        </dd>
      )
    );
  };

  // ラベル部分(1・2階層目)をレンダリングする関数
  const renderMenu = () => {
    // childrenの有無によって表示するラベルの取得方法分岐
    const label = children
      ? inferenceFactor.issueFactor.label // (label = 要因項目)
      : inferenceFactor.issueFactor.repairFactor.label; // (label = 解決方法)
    // 「Not Fixed」が選択されているかどうか
    const isNotFixed = isBoxChecked.isSelected && !isBoxChecked.isFixed;
    return (
      <div
        // className={`grow line-clamp-2 ${isNotFixed ? "" : "cursor-pointer"}`}
        className={`grow ${isNotFixed ? "" : "cursor-pointer"}`}
        onClick={isNotFixed ? undefined : toggleButton}
      >
        {label}
      </div>
    );
  };

  // 各リスト部分をレンダリングする関数
  const renderList = () => {
    // 「Not Fixed」が選択されているかどうか
    const isNotFixed = isBoxChecked.isSelected && !isBoxChecked.isFixed;

    return isNotFixed ? (
      // 「Not Fixed」が選択された場合
      <div>
        <dt
          className={`flex items-center justify-between border-gray-700 py-2
            ${children ? "pl-3" : "pl-8 pr-3"}`}
        >
          <div className="pr-3">{<FaPlus />}</div>
          {renderMenu()}
          {children && (
            <div className="px-3">
              <PiSquareDuotone size={"1.25rem"} />
            </div>
          )}
        </dt>
        {open && (children || renderReferences())}
      </div>
    ) : (
      // 「Not Fixed」が選択されていない場合
      <div>
        <dt
          className={`flex items-center justify-between border-gray-700  cursor-pointer py-2
            ${children ? "pl-3" : "pl-8 pr-3"}
            ${open && "border-b"}
            ${isMenuSelected && isBoxChecked.isSelected && "bg-sub-orange"}`}
        >
          <div className="pr-3 cursor-pointer" onClick={toggleButton}>
            {open ? <FaMinus /> : <FaPlus />}
          </div>
          {renderMenu()}
          {children &&
            // 一番上のリストの場合のみ処理
            (isBoxChecked.isSelected ? (
              // 上部のチェックボックスが選択されている時, □は白で選択可
              <div className="px-3 cursor-pointer" onClick={toggleSelectMenu}>
                {isMenuSelected ? (
                  <PiCheckSquare size={"1.25rem"} />
                ) : (
                  <PiSquare size={"1.25rem"} />
                )}
              </div>
            ) : (
              // 上部のチェックボックスが選択されていない時, □は灰色で選択不可
              <div className="px-3 cursor-default">
                <PiSquareDuotone size={"1.25rem"} />
              </div>
            ))}
        </dt>
        {open && (children || renderReferences())}
      </div>
    );
  };

  return (
    <dl
      className={`border-gray-700 rounded ${
        isBoxChecked.isSelected && !isBoxChecked.isFixed
          ? "bg-gray-300"
          : "bg-white"
      } ${children ? "border mb-4" : ""}`}
    >
      {renderList()}
    </dl>
  );
};
